/**
 * CoverageReviewPanel — Officer-facing candidate review for coverage requests.
 * Sprint 16: FR-COV-03, FR-COV-04
 *
 * Displays ranked candidate list with score bars, AI explanations, and
 * controls for excluding candidates and approving outreach.
 *
 * Brand: dark navy #0F1B2D, gold #D4A843 accent, DCVFD red #C8322B destructive
 */

import { useState, useEffect } from 'react';
import type { CoverageCandidate, CoverageRequest } from '../hooks/useCoverage';
import { useCoverage } from '../hooks/useCoverage';

interface CoverageReviewPanelProps {
  requestId: number;
  onApproved?: () => void;
  onCancelled?: () => void;
  onOpenChat?: () => void;
}

const FACTOR_LABELS: Record<string, string> = {
  battalion: 'Battalion',
  losap: 'LOSAP Need',
  pickup_rate: 'Pickup Rate',
  recency: 'Recency',
  response_speed: 'Response Speed',
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  open: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)', label: 'Open' },
  officer_review: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)', label: 'Needs Review' },
  outreach: { color: '#D4A843', bg: 'rgba(212, 168, 67, 0.12)', label: 'Outreach Active' },
  filled: { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.12)', label: 'Filled' },
  cancelled: { color: '#666', bg: 'rgba(102, 102, 102, 0.12)', label: 'Cancelled' },
};

export default function CoverageReviewPanel({
  requestId,
  onApproved,
  onCancelled,
  onOpenChat,
}: CoverageReviewPanelProps) {
  const { fetchRequestDetail, approveOutreach, cancelRequest } = useCoverage();
  const [detail, setDetail] = useState<{
    request: CoverageRequest;
    shift: Record<string, unknown>;
    requesting_member: Record<string, unknown>;
    candidates: CoverageCandidate[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [outreachMode, setOutreachMode] = useState<'simultaneous' | 'sequential'>('simultaneous');
  const [sequentialTimeout, setSequentialTimeout] = useState(30);
  const [officerNotes, setOfficerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDetail();
  }, [requestId]);

  const loadDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRequestDetail(requestId);
      setDetail(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await approveOutreach(requestId, {
        excluded_member_ids: Array.from(excludedIds),
        outreach_mode: outreachMode,
        sequential_timeout_minutes: outreachMode === 'sequential' ? sequentialTimeout : undefined,
        officer_notes: officerNotes || undefined,
      });
      onApproved?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this coverage request?')) return;
    setSubmitting(true);
    try {
      await cancelRequest(requestId, officerNotes || undefined);
      onCancelled?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExclude = (memberId: number) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  if (loading) {
    return <div style={{ padding: 24, color: '#999', textAlign: 'center' }}>Loading coverage request...</div>;
  }

  if (error || !detail) {
    return <div style={{ padding: 24, color: '#C8322B', textAlign: 'center' }}>{error || 'Failed to load'}</div>;
  }

  const { request: req, shift, requesting_member, candidates } = detail;
  const statusCfg = STATUS_CONFIG[(req.status as string) || 'open'] || STATUS_CONFIG.open;

  const shiftDate = shift.shift_start
    ? new Date(shift.shift_start as string).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York',
      })
    : '';
  const shiftTime = shift.shift_start
    ? new Date(shift.shift_start as string).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/New_York',
      })
    : '';

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      {/* Shift Summary */}
      <div
        style={{
          backgroundColor: '#0F1B2D',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
          border: '1px solid rgba(212, 168, 67, 0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ margin: 0, color: '#D4A843', fontSize: 18, fontWeight: 700 }}>Coverage Request</h2>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 700,
                color: '#8B5CF6',
                backgroundColor: 'rgba(139, 92, 246, 0.12)',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              AI-Powered
            </span>
          </div>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              color: statusCfg.color,
              backgroundColor: statusCfg.bg,
            }}
          >
            {statusCfg.label}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, color: '#ccc', fontSize: 14 }}>
          <div>
            <span style={{ color: '#888' }}>Date: </span>
            {shiftDate}
          </div>
          <div>
            <span style={{ color: '#888' }}>Time: </span>
            {shiftTime}
          </div>
          <div>
            <span style={{ color: '#888' }}>Battalion: </span>
            {shift.battalion_name as string}
          </div>
          <div>
            <span style={{ color: '#888' }}>Type: </span>
            {shift.shift_type as string}
          </div>
          <div>
            <span style={{ color: '#888' }}>Requested by: </span>
            {requesting_member.display_name as string}
          </div>
          {req.position_needed && (
            <div>
              <span style={{ color: '#888' }}>Position: </span>
              {(req.position_needed as string).replace(/_/g, ' ')}
            </div>
          )}
        </div>
        {req.member_reason && (
          <div style={{ marginTop: 8, color: '#999', fontSize: 13, fontStyle: 'italic' }}>
            "{req.member_reason as string}"
          </div>
        )}
      </div>

      {/* Candidate List */}
      <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>
        Ranked Candidates ({candidates.length})
      </h3>

      {candidates.length === 0 ? (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            color: '#999',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 8,
          }}
        >
          No eligible candidates found. Manual coordination required.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {candidates.map((c) => {
            const breakdown = JSON.parse(c.score_breakdown_json) as Record<string, number>;
            const isExcluded = excludedIds.has(c.member_id) || c.excluded_by_officer === 1;

            return (
              <div
                key={c.id}
                style={{
                  backgroundColor: isExcluded ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.04)',
                  borderRadius: 10,
                  padding: 16,
                  opacity: isExcluded ? 0.5 : 1,
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}
                >
                  <div>
                    <span style={{ color: '#D4A843', fontWeight: 700, marginRight: 8 }}>#{c.rank}</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{c.display_name}</span>
                    {c.battalion_name && (
                      <span style={{ color: '#888', fontSize: 13, marginLeft: 8 }}>{c.battalion_name}</span>
                    )}
                  </div>
                  <span style={{ color: '#D4A843', fontWeight: 700, fontSize: 18 }}>{c.score}</span>
                </div>

                {/* Score bar */}
                <div
                  style={{
                    width: '100%',
                    height: 6,
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: 3,
                    marginBottom: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${c.score}%`,
                      height: '100%',
                      backgroundColor: '#D4A843',
                      borderRadius: 3,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>

                {/* Factor chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {Object.entries(breakdown).map(([key, value]) => (
                    <span
                      key={key}
                      style={{
                        fontSize: 11,
                        padding: '2px 6px',
                        borderRadius: 4,
                        backgroundColor: 'rgba(212, 168, 67, 0.1)',
                        color: '#D4A843',
                      }}
                    >
                      {FACTOR_LABELS[key] || key}: {value}
                    </span>
                  ))}
                </div>

                {/* AI explanation */}
                {c.ai_explanation && (
                  <div style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic', marginBottom: 8 }}>
                    {c.ai_explanation}
                  </div>
                )}

                {/* Response status or exclude toggle */}
                {c.response ? (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: c.response === 'accepted' ? '#22C55E' : c.response === 'declined' ? '#EF4444' : '#666',
                    }}
                  >
                    {c.response.toUpperCase()}
                  </span>
                ) : (
                  req.status === 'officer_review' && (
                    <button
                      type="button"
                      onClick={() => toggleExclude(c.member_id)}
                      style={{
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 4,
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        backgroundColor: isExcluded ? 'rgba(200, 50, 43, 0.15)' : 'transparent',
                        color: isExcluded ? '#C8322B' : '#999',
                        cursor: 'pointer',
                      }}
                    >
                      {isExcluded ? 'Excluded' : 'Exclude'}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approval controls — only show in officer_review status */}
      {req.status === 'officer_review' && (
        <div
          style={{
            marginTop: 20,
            padding: 20,
            backgroundColor: '#0F1B2D',
            borderRadius: 12,
            border: '1px solid rgba(212, 168, 67, 0.15)',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <span style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 4 }}>Outreach Mode</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['simultaneous', 'sequential'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setOutreachMode(mode)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: outreachMode === mode ? '1px solid #D4A843' : '1px solid rgba(255, 255, 255, 0.15)',
                    backgroundColor: outreachMode === mode ? 'rgba(212, 168, 67, 0.12)' : 'transparent',
                    color: outreachMode === mode ? '#D4A843' : '#999',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {mode === 'simultaneous' ? 'All at Once' : 'One at a Time'}
                </button>
              ))}
            </div>
          </div>

          {outreachMode === 'sequential' && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 4 }}>
                Timeout per candidate (minutes)
              </span>
              <input
                type="number"
                min={5}
                max={120}
                step={5}
                value={sequentialTimeout}
                onChange={(e) => setSequentialTimeout(Math.max(5, parseInt(e.target.value, 10) || 30))}
                style={{
                  width: 100,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 6,
                  color: '#fff',
                  padding: '8px 10px',
                  fontSize: 14,
                }}
              />
              <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>
                Wait this long before contacting next candidate
              </span>
            </div>
          )}

          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ color: '#aaa', fontSize: 13, display: 'block', marginBottom: 4 }}>
              Officer Notes (optional)
            </span>
            <textarea
              value={officerNotes}
              onChange={(e) => setOfficerNotes(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 6,
                color: '#fff',
                padding: '8px 10px',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #C8322B',
                borderRadius: 8,
                color: '#C8322B',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Cancel Request
            </button>
            {onOpenChat && (
              <button
                type="button"
                onClick={onOpenChat}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  color: '#999',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                AI Assistant
              </button>
            )}
            <button
              type="button"
              onClick={handleApprove}
              disabled={submitting || candidates.length === 0}
              style={{
                flex: 1,
                padding: '10px 16px',
                backgroundColor: '#D4A843',
                border: 'none',
                borderRadius: 8,
                color: '#0F1B2D',
                fontSize: 14,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting || candidates.length === 0 ? 0.5 : 1,
              }}
            >
              {submitting ? 'Sending...' : 'Approve & Begin Outreach'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
